import { LicenceOrganisationSpecialisation } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationSpecialisation";
import { PatientRefIntervenorWithDetails } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { IReferralDoctorModel } from "@/Common/Models/IReferralDoctorModel";
import { Person } from "@/Common/Models/Person/Person";
import { DirectoryEntityType, DirectoryHealthCareProviderSearchDto } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectoryFaxNumber } from "./DirectoryFaxNumber";
import { DirectoryOtherEstablishment } from "./DirectoryOtherEstablishment";
import { IDirectoryEntity } from "./IDirectoryEntity";
import { IGetDisplayName } from "./IGetDisplayName";

export class DirectoryHealthCareProvider implements IGetDisplayName, IDirectoryEntity {
    public type: DirectoryEntityType = DirectoryEntityType.HealthcareProvider;
    public id: string;
    public firstName: string;
    public lastName: string;
    public gender: string;
    public statusDetails: string;
    public permitDetails: string;
    public specialtyDetails: string;
    public licenseOrganizationRuleId: string;
    public licenseOrganizationSpecializationId: string;
    public licenseNumber: string;
    public linkedEstablishments: DirectoryOtherEstablishment[];
    public linkedFaxNumbers: DirectoryFaxNumber[];
    public establishmentLinkedToRawInformationId: string | null;
    public rawAddress: string;
    public rawPhoneNumber: string;
    public faxNumber?: DirectoryFaxNumber;
    public isApproved?: boolean;
    public isSuggestion?: boolean;

    public orderNumber?: number;
    // UI only
    public selectedExternalFaxNumber: DirectoryFaxNumber;
    public isExternalConsultant: boolean = false;

    public constructor(init?: Partial<DirectoryHealthCareProvider>) {
        Object.assign(this, init);
        this.type = DirectoryEntityType.HealthcareProvider;
    }

    public getDefaultFaxNumberToSelect(): DirectoryFaxNumber | null {
        return this.faxNumber ?? this.linkedEstablishments.firstOrDefault()?.faxNumbers?.firstOrDefault();
    }

    public static fromSearchDto(dto: DirectoryHealthCareProviderSearchDto, orderNumber?: number): DirectoryHealthCareProvider {
        if (!dto)
            return null;

        const linkedEstablishments = dto.linkedEstablishments?.map(p => DirectoryOtherEstablishment.fromSearchDto(p)) ?? [];

        return new DirectoryHealthCareProvider({
            ...dto,
            faxNumber: new DirectoryFaxNumber(dto.faxNumber),
            linkedEstablishments: linkedEstablishments ?? [],
            linkedFaxNumbers: linkedEstablishments?.flatMap(p => p.faxNumbers) ?? [],
            orderNumber
        });
    }

    public static fromPatientRefIntervenor(dto: PatientRefIntervenorWithDetails): DirectoryHealthCareProvider {
        if (!dto?.HealthCareProvider)
            return null;

        const healthCareProvider = DirectoryHealthCareProvider.fromSearchDto(dto.HealthCareProvider);
        healthCareProvider.isExternalConsultant = true;
        const establishmentsFaxNumbers = healthCareProvider.linkedEstablishments?.flatMap(p => p.faxNumbers);
        const faxNumbers = [healthCareProvider?.faxNumber, ...establishmentsFaxNumbers].filter(p => !!p);
        healthCareProvider.selectedExternalFaxNumber = faxNumbers?.firstOrDefault(p => p.id === dto.Intervenor?.DirectoryFaxNumberId);

        return healthCareProvider;
    }

    public getRawFaxNumber(): string {
        return this.faxNumber ? this.faxNumber.number : null;
    }

    public getRawPhoneNumber(): string {
        return this.rawPhoneNumber ? this.rawPhoneNumber : null;
    }

    public getDirectoryAddressForDisplay(): string {
        return this.rawAddress ?? "";
    }

    public getFaxNumbersForDisplay(): { formattedNumber: string, description?: string }[] {
        return this.faxNumber
            ? [{ formattedNumber: PhoneNumberHelper.format(this.faxNumber.number, this.faxNumber.countryId) }]
            : [];
    }

    public getAllFaxNumbers(): DirectoryFaxNumber[] {
        return [this.faxNumber, ...this.linkedFaxNumbers].filter(p => !!p);
    }

    public hasFaxNumbers(): boolean {
        return !!this.faxNumber;
    }

    public hasPhoneNumbers(): boolean {
        return !!this.rawPhoneNumber;
    }

    public getFaxNumbersModels(): DirectoryFaxNumber[] {
        return [this.faxNumber];
    }

    public shouldRenderMultipleFaxNumbers(): boolean {
        return false;
    }

    public getPhoneNumbersForDisplay(includeLinkedEntities: boolean = false): string[] {
        const countryCode = cacheStore.LicenceOrganisationRulesById[this.licenseOrganizationRuleId]?.CountryCode;
        const countryId = cacheStore.getCountryMedfarDictionariesByISOCodeAlpha2(countryCode)?.Id;

        return [
            PhoneNumberHelper.format(this.rawPhoneNumber, countryId),
            ...(includeLinkedEntities
                ? this.linkedEstablishments?.flatMap(phones => phones.phoneNumbers.map(p => PhoneNumberHelper.formatDirectoryNumber(p)))
                : []
            )
        ]
            .filter(p => !IsNullOrWhiteSpace(p));
    }

    public getLinkedDirectoryEntities(): IDirectoryEntity[] {
        return this.linkedEstablishments ?? [];
    }

    public getDisplayName(): string {
        const specialty = cacheStore.TitlesAndSpecialtiesCache.find(p => p.Model.licenceOrganisationSpecialisationModel.Id === this.licenseOrganizationSpecializationId);
        const specialyString = LicenceOrganisationSpecialisation.getSpecialtyName(specialty?.Model?.licenceOrganisationSpecialisationModel);

        const organizationAcronym = cacheStore.LicenceOrganisationRulesById?.[this.licenseOrganizationRuleId]?.Acronym;

        const licenceNumber = !IsNullOrWhiteSpace(this.licenseNumber) ? `(${this.licenseNumber})` : "";

        let displayName = [this.firstName, this.lastName, licenceNumber].filter(p => !IsNullOrWhiteSpace(p)).join(" ");

        if (!IsNullOrWhiteSpace(specialyString))
            displayName += ` - ${specialyString}`;

        if (!IsNullOrWhiteSpace(organizationAcronym))
            displayName += ` - ${organizationAcronym}`;

        return displayName;
    }

    public static fromPerson(person: Person): DirectoryHealthCareProvider {
        if (!person)
            return null;

        return new DirectoryHealthCareProvider({
            licenseNumber: person.Licence,
            firstName: person.FirstName,
            lastName: person.LastName,
            licenseOrganizationRuleId: person.OrganizationRuleId,
            rawAddress: person.GetFormattedAddress(),
            rawPhoneNumber: person.PreferedPhone
        });
    }

    public static toPerson(healthCareProvider: DirectoryHealthCareProvider): Person {
        if (!healthCareProvider)
            return null;

        return new Person({
            Licence: healthCareProvider.licenseNumber,
            FirstName: healthCareProvider.firstName,
            LastName: healthCareProvider.lastName,
            OrganizationRuleId: healthCareProvider.licenseOrganizationRuleId,
            PreferedPhone: healthCareProvider.getPhoneNumbersForDisplay(true)?.firstOrDefault()
        });
    }

    public static toDisplayMultipleLinkedEstablishments(dto: DirectoryOtherEstablishment[]): DirectoryOtherEstablishment[] {
        const linkedEstablishment: DirectoryOtherEstablishment[] = [];

        dto?.map(establishment => {
            establishment.faxNumbers?.map(item => {
                let newEstablishment = new DirectoryOtherEstablishment(establishment);
                newEstablishment.faxNumbers = [item];
                linkedEstablishment.push(newEstablishment);
            });
        });

        return linkedEstablishment;
    }

    public static toReferralDoctorModel(provider: DirectoryHealthCareProvider): IReferralDoctorModel {
        return {
            FirstName: provider.firstName,
            LastName: provider.lastName,
            Gender: provider.gender,
            StatusDetails: provider.statusDetails,
            PermitDetails: provider.permitDetails,
            LicenceNumber: provider.licenseNumber,
            LicenceOrganisationRuleId: provider.licenseOrganizationRuleId,
            LicenceOrganisationSpecialisationId: provider.licenseOrganizationSpecializationId,
            Address: provider.rawAddress,
            SpecialityDetails: provider.specialtyDetails,
            Phone: provider.getPhoneNumbersForDisplay(true).firstOrDefault()
        };
    }

    public static isEqual(h1: DirectoryHealthCareProvider, h2: DirectoryHealthCareProvider): boolean {
        if (h1 === null && h2 === null)
            return true;

        if (!h1 || !h2)
            return false;

        let isSame = h1.firstName === h2.firstName
            && h1.lastName === h2.lastName
            && h1.licenseOrganizationRuleId === h2.licenseOrganizationRuleId
            && h1.licenseOrganizationSpecializationId === h2.licenseOrganizationSpecializationId
            && h1.licenseNumber === h2.licenseNumber
            && h1.faxNumber?.id === h2.faxNumber?.id;

        const h1Linked = h1.linkedEstablishments.map(p => `${p.id}-${p.faxNumbers?.firstOrDefault()?.id}`).sort((a, b) => a.localeCompare(b));
        const h2Linked = h2.linkedEstablishments.map(p => `${p.id}-${p.faxNumbers?.firstOrDefault()?.id}`).sort((a, b) => a.localeCompare(b));

        return isSame && JSON.stringify(h1Linked) === JSON.stringify(h2Linked);
    }
}
